import { t } from '@transifex/native';
import type { SyntheticEvent } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { cssClasses, cssVarList } from 'pkg/css/utils';
import { parseDateStringAsLocal, toISODateString } from 'pkg/date';
import DateTime, {
	Calendar as DateTimeCalendar,
	Granularity,
	type CalendarDayType,
} from 'pkg/datetime';
import useComponentDidMount from 'pkg/hooks/useComponentDidMount';
import useMixedState from 'pkg/hooks/useMixedState';

import { CalendarDay } from 'components/form/calendar/calendar_day';
import { CalendarNavigation } from 'components/form/calendar/calendar_navigation';
import { CalendarWeekdays } from 'components/form/calendar/calendar_weekdays';
import {
	MARKED_DATE_DEFAULT_COLOR,
	RANGE_OPTION,
	type CalendarStateInterface,
	type CalendarNavigationHandlers,
	type MarkedDateGroup,
} from 'components/form/calendar/types';

import * as css from './styles.css';

const toISODate = (date: Date | undefined): string => {
	if (!date || Number.isNaN(date.getTime())) return '';
	return toISODateString(date);
};

interface CalendarProps {
	range?: boolean;
	// Toggle any number of individual days in and out of `selectedDates`, rather
	// than picking one date (default) or a from/to pair (`range`). Mutually
	// exclusive with `range`.
	multiple?: boolean;
	// Makes the weekday headings select every selectable day in their column.
	// Requires `multiple`, since it selects many dates at once.
	selectWeekdays?: boolean;
	selectable?: boolean;

	disableDatesBefore?: Date;
	disableDatesAfter?: Date;
	selectedDates?: Date[];
	highlightedDates?: Date[][];
	markedDates?: MarkedDateGroup[];
	renderedDate?: Date;

	onDateChange: (dates: Date[] | Date) => void;
	onRenderedDateChange?: (date: Date) => void;
}
export const Calendar = memo(
	({
		selectedDates = [],
		highlightedDates = [],
		markedDates = [],
		range = false,
		multiple = false,
		selectWeekdays = false,
		disableDatesAfter,
		disableDatesBefore,
		onDateChange,
		selectable = true,
		onRenderedDateChange,
		renderedDate: renderedDateProp,
	}: CalendarProps) => {
		const currentDate = new Date();
		const currentDateTime = new DateTime(currentDate);

		const [calendarState, setCalendarState] =
			useMixedState<CalendarStateInterface>({
				fromDateRefVal: toISODate(new Date(selectedDates[0])),
				toDateRefVal: toISODate(new Date(selectedDates[1])),
				yearInputRefVal: new DateTime(selectedDates[0]).toLocaleDateString({
					year: 'numeric',
				}),
				renderedDate: selectedDates[0] || new Date(),
				currentRangeOption: RANGE_OPTION.from,
				prevDate: new Date(),
				showYearPicker: false,
			});

		const fromDateRef = useRef<HTMLInputElement>(null);
		const toDateRef = useRef<HTMLInputElement>(null);
		const yearGridRef = useRef<HTMLDivElement>(null);
		const activeYearRef = useRef<HTMLButtonElement>(null);

		useEffect(() => {
			if (range) {
				setCalendarState({
					fromDateRefVal: toISODate(new Date(selectedDates[0])),
					toDateRefVal: toISODate(new Date(selectedDates[1])),
				});
			}
		}, [selectedDates[0], selectedDates[1]]);

		useEffect(() => {
			if (fromDateRef.current !== null) {
				fromDateRef.current.value = calendarState.fromDateRefVal;
			}
		}, [calendarState.fromDateRefVal]);

		useEffect(() => {
			if (toDateRef.current !== null) {
				toDateRef.current.value = calendarState.toDateRefVal;
			}
		}, [calendarState.toDateRefVal]);

		useComponentDidMount(() => {
			if (onRenderedDateChange) {
				onRenderedDateChange(calendarState.renderedDate);
			}
		});

		useEffect(() => {
			if (renderedDateProp) {
				setCalendarState({ renderedDate: renderedDateProp });
			}
		}, [renderedDateProp]);

		const sortedSelectedDates = useMemo(
			() => [...selectedDates].sort((a, b) => +a - +b),
			[selectedDates]
		);

		const checkDateSelectable = (dateTime: DateTime): boolean => {
			if (!selectable) return false;

			const hasDisableBeforeRange = disableDatesBefore instanceof Date;
			const hasDisableAfterRange = disableDatesAfter instanceof Date;

			if (hasDisableBeforeRange && hasDisableAfterRange) {
				const before = new Date(disableDatesBefore);
				const after = new Date(disableDatesAfter);
				before.setHours(0, 0, 0, 1);
				after.setHours(23, 59, 59, 999);
				return dateTime.isBetween(+before, +after);
			} else if (hasDisableBeforeRange && !hasDisableAfterRange) {
				const before = new Date(disableDatesBefore);
				before.setHours(0, 0, 0, 1);
				return dateTime.isAfter(+before);
			} else if (!hasDisableBeforeRange && hasDisableAfterRange) {
				const after = new Date(disableDatesAfter);
				after.setHours(23, 59, 59, 999);
				return dateTime.isBefore(+after);
			}

			return true;
		};

		const emitDateChange = useCallback(
			(selectedDate: Date, changeRangeOption = true) => {
				let dates = [selectedDate];

				if (multiple) {
					// Compare by day: an already-selected date carries a time-of-day
					// while the clicked date is midnight.
					const clicked = new DateTime(selectedDate);
					const isSelected = sortedSelectedDates.some((date) =>
						clicked.sameDay(+date)
					);

					if (isSelected) {
						dates = sortedSelectedDates.filter(
							(date) => !clicked.sameDay(+date)
						);
					} else {
						dates = [...sortedSelectedDates, selectedDate];
					}

					onDateChange(dates);

					return;
				}

				if (range) {
					const { currentRangeOption } = calendarState;

					dates = [...sortedSelectedDates];

					if (currentRangeOption === RANGE_OPTION.from) {
						dates = [selectedDate, selectedDates[1]];
					} else {
						dates = [selectedDates[0], selectedDate];
					}

					dates.sort((a, b) => +a - +b);

					if (changeRangeOption) {
						setCalendarState({
							currentRangeOption:
								currentRangeOption === RANGE_OPTION.from
									? RANGE_OPTION.to
									: RANGE_OPTION.from,
						});
					}
				}

				onDateChange(dates);
			},
			[
				range,
				multiple,
				calendarState.currentRangeOption,
				selectedDates,
				sortedSelectedDates,
				onDateChange,
			]
		);

		const onNavigatePrev = useCallback(() => {
			const { renderedDate } = calendarState;
			const prevRenderedDate = new DateTime(renderedDate)
				.setDay(1)
				.prev(Granularity.month)
				.toDate();

			setCalendarState({
				renderedDate: prevRenderedDate,
			});

			if (onRenderedDateChange) {
				onRenderedDateChange(prevRenderedDate);
			}
		}, [calendarState.renderedDate, onRenderedDateChange]);

		const onNavigateNext = useCallback(() => {
			const { renderedDate } = calendarState;
			const nextRenderedDate = new DateTime(renderedDate)
				.setDay(1)
				.next(Granularity.month)
				.toDate();

			setCalendarState({
				renderedDate: nextRenderedDate,
			});

			if (onRenderedDateChange) {
				onRenderedDateChange(nextRenderedDate);
			}
		}, [calendarState.renderedDate, onRenderedDateChange]);

		const onToggleYearPicker = useCallback(() => {
			setCalendarState({
				showYearPicker: !calendarState.showYearPicker,
			});
		}, [calendarState.showYearPicker]);

		useEffect(() => {
			if (
				calendarState.showYearPicker &&
				activeYearRef.current &&
				yearGridRef.current
			) {
				const grid = yearGridRef.current;
				const button = activeYearRef.current;
				grid.scrollTop =
					button.offsetTop - grid.clientHeight / 2 + button.offsetHeight / 2;
			}
		}, [calendarState.showYearPicker]);

		const onSelectYear = useCallback(
			(year: number) => {
				const { renderedDate } = calendarState;
				const nextRenderedDate = new DateTime(renderedDate)
					.setYear(year)
					.toDate();

				setCalendarState({
					renderedDate: nextRenderedDate,
					showYearPicker: false,
				});

				if (onRenderedDateChange) {
					onRenderedDateChange(nextRenderedDate);
				}
			},
			[calendarState.renderedDate, onRenderedDateChange]
		);

		const onNavigateToday = useCallback(() => {
			const nextRenderedDate = DateTime.now().toDate();

			setCalendarState({
				renderedDate: nextRenderedDate,
			});
		}, []);

		const renderedCalendarIsCurrent = useMemo(
			() =>
				calendarState.renderedDate.getMonth() === currentDate.getMonth() &&
				calendarState.renderedDate.getFullYear() === currentDate.getFullYear(),
			[currentDate, calendarState.renderedDate]
		);

		const renderNavigationTodayButton = useMemo(() => {
			const ts = currentDateTime.getUnixTimestamp();
			const disableAfterDate = disableDatesAfter
				? Math.ceil(
						new Date(disableDatesAfter).setHours(23, 59, 59, 999) / 1000
					)
				: ts;

			const disableBeforeDate = disableDatesBefore
				? Math.ceil(new Date(disableDatesBefore).setHours(0, 0, 0, 1) / 1000)
				: ts;

			return (
				!renderedCalendarIsCurrent &&
				ts <= disableAfterDate &&
				ts >= disableBeforeDate
			);
		}, [
			currentDateTime,
			disableDatesAfter,
			disableDatesBefore,
			renderedCalendarIsCurrent,
		]);

		const calendarData = useMemo(() => {
			const { renderedDate } = calendarState;
			const year = renderedDate.getFullYear();
			const month = renderedDate.getMonth() + 1;
			return DateTimeCalendar.generate(year, month);
		}, [calendarState.renderedDate]);

		const onInputFocus = useCallback(
			(event: SyntheticEvent<HTMLInputElement>) => {
				const rangeOption = event.currentTarget.dataset
					.rangeoption as RANGE_OPTION;
				const d = parseDateStringAsLocal(event.currentTarget.value.trim());

				if (d === null) {
					setCalendarState({ currentRangeOption: rangeOption });
					return;
				}

				setCalendarState({
					renderedDate: d,
					prevDate: d,
					currentRangeOption: rangeOption,
				});
			},
			[]
		);

		const onInputBlur = useCallback(
			(event: SyntheticEvent<HTMLInputElement>) => {
				const parsed = parseDateStringAsLocal(event.currentTarget.value.trim());
				const pd = new DateTime(new Date(calendarState.prevDate));

				if (parsed === null) {
					event.currentTarget.value = toISODate(pd.dateTime);
					return;
				}

				const d = new DateTime(parsed);
				if (d.startOfDay !== pd.startOfDay) {
					emitDateChange(d.dateTime, false);
					setCalendarState({ renderedDate: d.dateTime });
				}
			},
			[calendarState.prevDate, emitDateChange]
		);

		const selectableDays = useMemo(() => {
			const set = new Set<number>();
			if (!selectable) return set;

			for (const day of calendarData) {
				const dt = new DateTime(new Date(day.timestamp));
				if (checkDateSelectable(dt)) {
					set.add(day.timestamp);
				}
			}
			return set;
		}, [calendarData, selectable, disableDatesBefore, disableDatesAfter]);

		// Selects a whole weekday column: every selectable day of the rendered
		// month that falls on it. Already-complete columns clear instead, so the
		// heading toggles the same way a day cell does.
		const onSelectWeekday = useCallback(
			(weekdayIndex: number) => {
				const columnDays = calendarData.filter(
					(day, index) =>
						index % 7 === weekdayIndex &&
						day.isCurrentMonth &&
						selectableDays.has(day.timestamp)
				);

				if (columnDays.length === 0) {
					return;
				}

				const isSelected = (day: CalendarDayType) =>
					selectedDates.some((date) =>
						new DateTime(new Date(day.timestamp)).sameDay(+date)
					);

				const allSelected = columnDays.every(isSelected);

				let dates: Date[];
				if (allSelected) {
					dates = selectedDates.filter(
						(date) =>
							!columnDays.some((day) =>
								new DateTime(new Date(day.timestamp)).sameDay(+date)
							)
					);
				} else {
					const missing = columnDays
						.filter((day) => !isSelected(day))
						.map((day) => new Date(day.timestamp));

					dates = [...selectedDates, ...missing];
				}

				onDateChange(dates);
			},
			[calendarData, selectableDays, selectedDates, onDateChange]
		);

		const legendGroups = markedDates.filter(
			(group) => group.label && group.dates.length > 0
		);

		const handlers: CalendarNavigationHandlers = {
			onNavigatePrev,
			onNavigateNext,
			onNavigateToday,
			onToggleYearPicker,
			onSelectYear,
			onInputFocus,
			onInputBlur,
		};

		return (
			<div className={css.wrapper}>
				<CalendarNavigation
					range={range}
					calendarState={calendarState}
					fromDateRef={fromDateRef}
					toDateRef={toDateRef}
					handlers={handlers}
				/>
				<div className={css.calendarBody}>
					{calendarState.showYearPicker ? (
						<div className={css.yearGrid} ref={yearGridRef}>
							{Array.from({ length: 100 }, (_, i) => {
								const year = currentDate.getFullYear() - 80 + i;
								const isActive =
									year === calendarState.renderedDate.getFullYear();
								const isCurrent = year === currentDate.getFullYear();
								return (
									<button
										key={year}
										type="button"
										ref={isActive ? activeYearRef : undefined}
										className={cssClasses(
											css.yearCell,
											isActive ? css.yearCellActive : '',
											isCurrent ? css.yearCellCurrent : ''
										)}
										onClick={() => onSelectYear(year)}>
										{year}
									</button>
								);
							})}
						</div>
					) : (
						<div className={css.monthWrapper}>
							<CalendarWeekdays
								calendar={calendarData}
								onSelectWeekday={
									selectWeekdays && multiple ? onSelectWeekday : undefined
								}
							/>
							<article className={css.body}>
								<div className={css.month}>
									{calendarData.map((calendarDay, index) => (
										<CalendarDay
											key={index}
											calendarDay={calendarDay}
											index={index}
											range={range}
											multiple={multiple}
											selectable={selectable}
											selectedDates={selectedDates}
											highlightedDates={highlightedDates}
											markedDates={markedDates}
											isSelectable={selectableDays.has(calendarDay.timestamp)}
											emitDateChange={emitDateChange}
										/>
									))}
								</div>
							</article>
						</div>
					)}
				</div>
				{legendGroups.length > 0 && (
					<div className={css.markedLegend}>
						{legendGroups.map((group, groupIndex) => (
							<div key={groupIndex} className={css.markedLegendItem}>
								<span
									className={css.markedLegendDot}
									style={cssVarList({
										'legend-dot-color':
											group.color ?? MARKED_DATE_DEFAULT_COLOR,
									})}
								/>
								{group.label}
							</div>
						))}
					</div>
				)}
				{/* The row is always rendered so the calendar keeps one height while
				    navigating months; only the link inside it is conditional. */}
				<div className={css.todayFooter}>
					{renderNavigationTodayButton && (
						<span
							className={css.todayButton}
							onClick={handlers.onNavigateToday}
							data-testid="datetime_picker.today">
							{t('Today')}
						</span>
					)}
				</div>
			</div>
		);
	}
);
